import { type UseQueryResult, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { Snippet } from '@/components/common/Snippet';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useRecordingLabel } from '@/hooks/useRecordingLabel';
import { audioAt, clock } from '@/lib/audio';
import { ThesaurusNotice } from '@/pages/cade/ThesaurusNotice';
import { cadeAudioParams, fetchCadeAudioRecordings, fetchCadeAudioSearch } from '@/services/cade';
import type {
  CadeAudioCoverage,
  CadeAudioHit,
  CadeAudioRecordingList,
  CadeAudioRecordingRow,
  CadeAudioSearch,
} from '@/services/types';

const PAGE_SIZE = 20;

/** The search term and year live in the URL, so a search can be shared and survives a reload. */
function useAudioFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(searchParams.get('q') ?? '');
  const set = (name: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(name, value);
    else next.delete(name);
    setSearchParams(next);
  };
  return {
    draft,
    setDraft,
    query: searchParams.get('q') ?? '',
    year: searchParams.get('year') ?? '',
    setYear: (value: string) => set('year', value),
    submit: (event: FormEvent) => {
      event.preventDefault();
      set('q', draft.trim());
    },
  };
}

/**
 * What was SAID in the Tribunal's recorded sessions: a search over machine transcripts and the
 * list of recordings, each opening onto the votes spoken in it.
 *
 * The screen never lets a transcript pass for the minutes. A notice above the search says so,
 * every passage carries the second it starts at and a link that starts CADE's audio there, and
 * the first line under the search states how few of the published recordings are transcribed.
 */
export function CadeAudioPage() {
  const { t } = useTranslation(['cade', 'common']);
  const { draft, setDraft, query, year, setYear, submit } = useAudioFilters();

  const recordings = useQuery({
    queryKey: ['cade', 'audio', 'recordings'],
    queryFn: fetchCadeAudioRecordings,
  });

  const coverage = recordings.data?.coverage ?? null;
  const exportParams = query ? cadeAudioParams({ query, year: year ? Number(year) : null }) : null;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:audio.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:audio.subtitle')}</p>
      </header>

      <p
        role="note"
        className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground"
        data-machine-transcript
      >
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        {t('cade:audio.notice')}
      </p>

      <ExplainerCard i18nKey="cade:audio.explainer" />
      {recordings.data ? <AsOfStrip asOf={recordings.data.as_of} /> : null}

      <SearchForm
        draft={draft}
        onDraft={setDraft}
        year={year}
        onYear={setYear}
        years={coverage?.years ?? []}
        onSubmit={submit}
      />

      <CoverageLine coverage={coverage} />
      <Link
        to="/cade/audio/topics"
        className="inline-block text-sm text-primary underline-offset-4 hover:underline"
        data-topics-link
      >
        {t('cade:audio.topicsLink')}
      </Link>

      <ResultsOrPrompt query={query} year={year} exportParams={exportParams} />

      <RecordingList query={recordings} />
      <FieldGuide i18nKey="cade:audio.guide" />
    </div>
  );
}

function CoverageLine({ coverage }: { coverage: CadeAudioCoverage | null }) {
  const { t } = useTranslation(['cade']);
  if (!coverage) return null;
  return (
    <p className="text-xs text-muted-foreground" data-audio-coverage>
      {t('cade:audio.coverage', {
        transcribed: coverage.recordings_transcribed,
        available: coverage.recordings_available,
        hours: coverage.hours,
      })}
    </p>
  );
}

function ResultsOrPrompt({
  query,
  year,
  exportParams,
}: {
  query: string;
  year: string;
  exportParams: URLSearchParams | null;
}) {
  const { t } = useTranslation(['cade']);
  if (!query) return <p className="text-sm text-muted-foreground">{t('cade:audio.prompt')}</p>;
  return (
    <SearchResults
      query={query}
      year={year}
      exportPath={
        exportParams ? `/api/v1/cade/export/audio/search?${exportParams.toString()}` : null
      }
    />
  );
}

function SearchForm({
  draft,
  onDraft,
  year,
  onYear,
  years,
  onSubmit,
}: {
  draft: string;
  onDraft: (value: string) => void;
  year: string;
  onYear: (value: string) => void;
  years: number[];
  onSubmit: (event: FormEvent) => void;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4"
    >
      <label className="block min-w-[16rem] flex-1 space-y-1">
        <span className="text-xs font-medium text-foreground">{t('cade:audio.search')}</span>
        <input
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          placeholder={t('cade:audio.placeholder')}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>
      <label className="block min-w-[8rem] space-y-1">
        <span className="text-xs font-medium text-foreground">{t('cade:audio.year')}</span>
        <select
          value={year}
          onChange={(event) => onYear(event.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('cade:audio.allYears')}</option>
          {years.map((value) => (
            <option key={value} value={String(value)}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <button
        type="submit"
        className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
      >
        {t('cade:audio.searchButton')}
      </button>
    </form>
  );
}

function SearchResults({
  query,
  year,
  exportPath,
}: {
  query: string;
  year: string;
  exportPath: string | null;
}) {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cade', 'audio', 'search', query, year],
      queryFn: ({ pageParam }) =>
        fetchCadeAudioSearch({
          query,
          year: year ? Number(year) : null,
          limit: PAGE_SIZE,
          offset: pageParam,
        }),
      initialPageParam: 0,
      getNextPageParam: (last, all) => {
        const loaded = all.reduce((n, page) => n + (page.search.value?.hits.length ?? 0), 0);
        const total = last.search.value?.total ?? 0;
        return loaded < total ? loaded : undefined;
      },
    });

  if (isPending) return <SkeletonLoader rows={4} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  const first = data.pages[0];
  const hits = data.pages.flatMap((page) => page.search.value?.hits ?? []);

  return (
    <section className="space-y-3" data-audio-results>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{t('cade:audio.results')}</h2>
        {exportPath ? <ExportButton path={exportPath} /> : null}
      </div>
      <Answer answer={first.search} help={t('cade:audio.help')}>
        {(search: CadeAudioSearch) =>
          search.no_searchable_terms ? (
            <p role="alert" className="rounded-md border p-3 text-sm text-foreground">
              {t('cade:audio.noTerms')}
            </p>
          ) : (
            <div className="space-y-3">
              <ThesaurusNotice groups={search.expanded ?? []} />
              <p className="text-xs text-muted-foreground" data-audio-total>
                {t('cade:audio.matches', { count: search.total })}
              </p>
              {hits.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('cade:audio.noMatches')}</p>
              ) : (
                <ul className="space-y-3">
                  {hits.map((hit, index) => (
                    <HitCard key={`${hit.recording.key}-${hit.start_s}-${index}`} hit={hit} />
                  ))}
                </ul>
              )}
              {hasNextPage ? (
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={() => void fetchNextPage()}
                  className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
                >
                  {t('cade:audio.more')}
                </button>
              ) : null}
            </div>
          )
        }
      </Answer>
    </section>
  );
}

function HitCard({ hit }: { hit: CadeAudioHit }) {
  const { t } = useTranslation(['cade']);
  const label = useRecordingLabel();
  const link = audioAt(hit.recording.audio_url, hit.start_s);
  return (
    <li className="space-y-1 rounded-md border p-3" data-audio-hit>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <Link
          to={`/cade/audio/${hit.recording.key}`}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {label(hit.recording)}
        </Link>
        <span className="tabular-nums">{clock(hit.start_s)}</span>
      </div>
      {hit.before ? <p className="text-xs text-muted-foreground">{hit.before}</p> : null}
      <p className="text-sm text-foreground">
        <Snippet text={hit.text_marked} />
      </p>
      {hit.after ? <p className="text-xs text-muted-foreground">{hit.after}</p> : null}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            {t('cade:audio.listen', { time: clock(hit.start_s) })}
          </a>
        ) : null}
        <Link
          to={`/cade/audio/${hit.recording.key}/transcript#seg-${hit.seg}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {t('cade:audio.detail.seeInTranscript')}
        </Link>
      </div>
    </li>
  );
}

function RecordingList({ query }: { query: UseQueryResult<CadeAudioRecordingList> }) {
  const { t, i18n } = useTranslation(['cade']);
  const hours = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  const label = useRecordingLabel();
  if (query.isPending) return <SkeletonLoader rows={3} />;
  if (query.error)
    return <ErrorMessage message={query.error.message} onRetry={() => void query.refetch()} />;
  return (
    <section className="space-y-2" data-audio-recordings>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:audio.recordings.title')}</h2>
      <Answer answer={query.data.recordings} help={t('cade:audio.recordings.help')}>
        {(rows: CadeAudioRecordingRow[]) => (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t('cade:audio.recordings.columns.session')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:audio.recordings.columns.hours')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:audio.recordings.columns.results')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:audio.recordings.columns.calls')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:audio.recordings.columns.recusals')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.recording.key} className="border-t">
                    <td className="px-3 py-2">
                      <Link
                        to={`/cade/audio/${row.recording.key}`}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {label(row.recording)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {row.duration_s !== null ? hours.format(row.duration_s / 3600) : ''}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.results}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.vote_calls}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.recusals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Answer>
      <div className="flex justify-end">
        <ExportButton
          path="/api/v1/cade/export/audio/events"
          label={t('cade:audio.exportEvents')}
        />
      </div>
    </section>
  );
}
